/**
 * Tenant types matching backend enums exactly (PascalCase).
 *
 * Backend: WiCloud.Domain.Enums.TenantRole, WiCloud.Domain.Enums.TenantStatus
 * EF Core stores enums as strings via .HasConversion<string>().
 * DTOs return string values matching these enum member names.
 */

// TenantRole - canonical frontend roles
// Backend has Billing (3) and ReadOnly (4); mapped to Member and Viewer respectively.
// Billing users see "Member" in the UI — no UI features distinguish Billing from Member.
export const TENANT_ROLES = ["Owner", "Admin", "Member", "Viewer"] as const;
export type TenantRole = (typeof TENANT_ROLES)[number];

// TenantStatus - matches WiCloud.Domain.Enums.TenantStatus
export const TENANT_STATUSES = [
  "Pending",
  "Active",
  "Suspended",
  "Inactive",
] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

/**
 * Map backend TenantRole to canonical TenantRole.
 * Backend may return number (enum int) or string (enum name).
 */
export function mapTenantRole(backendRole?: string | number): TenantRole {
  if (typeof backendRole === "number") {
    const enumMap: Record<number, TenantRole> = {
      0: "Owner",
      1: "Admin",
      2: "Member",
      3: "Member",
      4: "Viewer",
    };
    return enumMap[backendRole] ?? "Member";
  }

  const roleMap: Record<string, TenantRole> = {
    owner: "Owner",
    admin: "Admin",
    member: "Member",
    billing: "Member",
    readonly: "Viewer",
    viewer: "Viewer",
  };
  return roleMap[backendRole?.toLowerCase() ?? ""] ?? "Member";
}

/**
 * Map backend TenantStatus to canonical TenantStatus.
 * Backend may return number (enum int) or string (enum name).
 */
export function mapTenantStatus(backendStatus?: string | number): TenantStatus {
  if (typeof backendStatus === "number") {
    const enumMap: Record<number, TenantStatus> = {
      0: "Pending",
      1: "Active",
      2: "Suspended",
      3: "Inactive",
    };
    return enumMap[backendStatus] ?? "Active";
  }

  const statusMap: Record<string, TenantStatus> = {
    pending: "Pending",
    active: "Active",
    suspended: "Suspended",
    inactive: "Inactive",
    trial: "Active",
  };
  return statusMap[backendStatus?.toLowerCase() ?? ""] ?? "Active";
}
